<script setup lang="ts">
import { computed } from "vue";

import LeArticleGender from "./LeArticleGender.vue";
import LeArticleWordClass from "./LeArticleWordClass.vue";
import type { ClausensArticle } from "./types";

const props = defineProps<{
  article: ClausensArticle;
  collapsible?: boolean;
}>();

const hasDictionaryCover = computed<boolean>(() => props.collapsible);
</script>

<template>
  <section class="le-article-header" :class="{ 'le-article-header--with-dictionary-cover': hasDictionaryCover }">
    <slot v-if="hasDictionaryCover" name="dictionaryCover"></slot>

    <div class="le-article-header__container">
      <span class="le-article-header__head-word">
        {{ props.article.lemma }}
      </span>

      <span v-if="props.article.synonyms" class="le-article-header__synonyms">
        ({{ props.article.synonyms.replaceAll(",", ", ") }})
      </span>

      <LeArticleWordClass
        v-if="props.article.wordClass"
        class="le-article-header__word-class"
        :word-class="props.article.wordClass"
      />

      <LeArticleGender v-if="props.article.gender" class="le-article-header__gender" :gender="props.article.gender" />

      <span v-if="props.article.inflexions" class="le-article-header__inflexions" title="Bøjninger">
        &lt;{{ props.article.inflexions }}&gt;
      </span>

      <span v-if="props.article.gramComment" class="le-article-header__gram-comment">
        ({{ props.article.gramComment }})
      </span>

      <span v-if="props.article.link" class="le-article-header__link-container">
        <span>se </span>
        <slot name="link" v-bind="{ word: props.article.link }">
          <span class="le-article-header__placeholder-link">
            {{ props.article.link }}
          </span>
        </slot>
        <span v-if="props.article.linkExplanation" title="Forklaring" class="le-article-header__link-explanation">
          ({{ props.article.linkExplanation }})
        </span>
      </span>
    </div>

    <div class="le-article-header__article-feedback-button">
      <slot name="feedbackButton" />
    </div>

    <div v-if="hasDictionaryCover" class="le-article-header__about-dictionary-button">
      <slot name="aboutDictionaryButton" />
    </div>
  </section>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/dictsys-article";

.le-article-header {
  display: flex;
  align-items: center;
  gap: 16px;

  #media-breakpoint-up(md, {
    flex-direction: row;
    justify-content: space-between;
    padding-top: 24px;
    padding-bottom: 24px;
  });

  &--with-dictionary-cover {
    flex-direction: row;
    padding: 10px 16px;

    #media-breakpoint-up(md, {
      justify-content: flex-start;
    });
  }

  &__container {
    display: flex;
    flex-direction: row;
    gap: 12px;
    align-items: center;
    width: 100%;
  }

  &__head-word {
    color: @colors[@primary][700];
    word-break: break-word;
    hyphens: auto;
    .dictsys-article-header();
  }

  &__word-class,
  &__gender,
  &__inflexions,
  &__gram-comment,
  &__link-explanation {
    .dictsys-article-text();
    color: @colors[@neutrals][300];
    font-size: 1rem;
    font-style: italic;

    #media-breakpoint-up(md, {
      font-size: 1.125rem;
    });
  }

  &__inflexions,
  &__gram-comment {
    color: @colors[@neutrals][200];
    font-size: 1rem;
  }

  &__gender {
    margin-left: -10px;
    font-size: 1rem;
  }

  &__link-container {
    :slotted(a),
    .le-article-header__placeholder-link {
      .link();
      color: @colors[@primary][700];
      text-decoration: underline;
      cursor: pointer;

      &.focus-visible,
      &:hover {
        color: @colors[@primary][700];
      }
    }
  }

  &__link-explanation {
    font-size: 1rem;
    font-style: normal;
  }

  &__article-feedback-button {
    display: block;
  }
}
</style>
